import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import ListItemWrapper from '../../DatasetDefinitions/ListItemWrapper';
import Column from '../../DatasetDefinitions/Column';
import Typography from 'kingpin/atoms/Typography';
import getMetricDescription from '../../../../utils/metrics/getMetricDescription';
import MetricUsageLookupContext from '../../../../contexts/MetricUsageLookupContext';
import UserFromId from '../../../../components/UserFromId';
import MetricFiltersBar from '../MetricFiltersBar';
import MetricListStyles from './styles';
import Button from 'kingpin/atoms/Button';
import ContentMenu from './ContentMenu';
import SPECIFIC_SET_ROUTES from '../../SpecificSet/routes';
import CoreBadge from '../CoreBadge';
import AiGeneratedBadge from '../../../../components/AiGeneratedBadge';
import MetricListHeaders from './MetricListHeaders';
import NoMetrics from './NoMetrics';
import LoadingMetrics from './LoadingMetrics';
import ConditionalLink from '../ConditionalLink';
import AnalyticsContext from '../../../../contexts/AnalyticsContext';
import useGetDatasetLabel from '../../../../components/Inputs/MetricPicker/MetricPickerPopup/hooks/useGetDatasetLabel';
import FleetOpsStaffOnly from '../../../../components/Common/FleetOpsStaffOnly';
import FieldLabelColumn from './FieldLabelColumn';
import DataManagerSpecificSetContext from '../../../../contexts/DataManagerSpecificSetContext';
import { PortalsContext } from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';

const MetricListItem = ({
  metric,
  openMetricSlideOut,
  openMetricPopup,
}: {
  metric: Metrics.NormalMetric;
  openMetricSlideOut: (metric: Metrics.NormalMetric) => void;
  openMetricPopup: (
    metric: Metrics.NormalMetric | Metrics.CompoundMetric,
  ) => void;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const getDatasetLabel = useGetDatasetLabel();
  const { metricLookup } = useContext(MetricUsageLookupContext);
  const getUsage = useCallback(() => {
    const l = metricLookup[metric.id];
    if (!l) {
      return '';
    }

    return l.usageCount.toString(10);
  }, [metric.id, metricLookup]);
  const [usage, setUsage] = useState(() => getUsage());
  useEffect(() => {
    setUsage(getUsage());
  }, [getUsage]);

  const onSelected = useCallback(() => {
    openMetricSlideOut(metric);
  }, [metric, openMetricSlideOut]);

  const onEditClicked = useCallback(() => {
    trackEvent('Metrics - Edit Clicked', { metricType: metric.type });
    openMetricPopup(metric);
  }, [metric, openMetricPopup, trackEvent]);

  return (
    <>
      <Column
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          flex: 2,
        }}
      >
        <Typography.Body type="Link" onClick={onSelected}>
          {metric.name}
        </Typography.Body>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {metric.isAIGenerated && <AiGeneratedBadge />}
          <CoreBadge metric={metric} />
        </div>
      </Column>
      <Column style={{ flex: 2 }}>
        <Typography.Body type="Body 12">
          {getMetricDescription(metric)}
        </Typography.Body>
      </Column>
      <Column>
        <ConditionalLink
          text={getDatasetLabel(metric.dataType)}
          to={SPECIFIC_SET_ROUTES.buildBase(metric.dataType, selectedPortal)}
          dataset={metric.dataType}
        />
      </Column>
      <FleetOpsStaffOnly>
        <FieldLabelColumn metric={metric} />
      </FleetOpsStaffOnly>
      <Column>
        <Typography.Body type="Body 12">{usage}</Typography.Body>
      </Column>
      <Column>
        <UserFromId userId={metric.updatedBy} />
      </Column>
      <Column
        style={{ flexDirection: 'row', justifyContent: 'flex-end' }}
        className="showOnMouseOver"
      >
        <div style={{ marginRight: 8 }}>
          <Button
            onClick={onEditClicked}
            label="Edit"
            testId={`edit-${metric.id}`}
            type="Ghost"
            size="Small"
          />
        </div>
        <ContentMenu metric={metric} openMetricPopup={openMetricPopup} />
      </Column>
    </>
  );
};

const MemoizedMetricListItem = memo(MetricListItem);

const NormalMetricList = ({
  isLoading,
  metrics,
  openMetricSlideOut,
  openMetricPopup,
  onCreateMetricClicked,
}: {
  isLoading: boolean;
  metrics: Metrics.NormalMetric[];
  openMetricSlideOut: (
    metric: Metrics.NormalMetric | Metrics.CompoundMetric,
  ) => void;
  openMetricPopup: (
    metric: Metrics.NormalMetric | Metrics.CompoundMetric,
  ) => void;
  onCreateMetricClicked: () => void;
}) => {
  const { isOnSpecificSet } = useContext(DataManagerSpecificSetContext);

  return (
    <MetricListStyles.Wrapper>
      <MetricFiltersBar mode={'Metric'} />
      <div>
        <MetricListHeaders />
        <MetricListStyles.ListWrapper isSpecificDataSet={isOnSpecificSet}>
          <MetricListStyles.List>
            {metrics.map((m) => (
              <ListItemWrapper key={m.id}>
                <MemoizedMetricListItem
                  metric={m}
                  key={m.id}
                  openMetricSlideOut={openMetricSlideOut}
                  openMetricPopup={openMetricPopup}
                />
              </ListItemWrapper>
            ))}
            {metrics.length === 0 && !isLoading && (
              <NoMetrics onCreateClicked={onCreateMetricClicked} />
            )}
            {metrics.length === 0 && isLoading && <LoadingMetrics />}
          </MetricListStyles.List>
        </MetricListStyles.ListWrapper>
      </div>
    </MetricListStyles.Wrapper>
  );
};

export default NormalMetricList;
